/**
 * Design tokens — centralized, typed values used across UI components.
 * Change here, reflected everywhere.
 */

// Status → color mapping. Every badge / chip that displays a CRM status
// should go through this so colors stay consistent.
export type StatusKey =
  | 'open' | 'draft' | 'sent' | 'proposal' | 'negotiation'
  | 'closed-won' | 'won' | 'closed-lost' | 'lost'
  | 'signed' | 'pending' | 'expired'
  | 'lead' | 'customer' | 'active' | 'inactive' | 'closed'
  | 'paid' | 'unpaid' | 'overdue' | 'warning';

export interface StatusStyle {
  label: string;      // Hebrew display label
  bg:    string;      // bg color class
  text:  string;      // text color class
  dot:   string;      // dot indicator color class
}

export const STATUS_STYLES: Record<StatusKey, StatusStyle> = {
  'warning':     { label: 'שים לב',      bg: 'bg-amber-100',    text: 'text-amber-900',   dot: 'bg-amber-600' },
  // Deal / Quote statuses
  'open':        { label: 'פתוח',       bg: 'bg-slate-100',    text: 'text-slate-700',   dot: 'bg-slate-500' },
  'draft':       { label: 'טיוטה',       bg: 'bg-slate-100',    text: 'text-slate-700',   dot: 'bg-slate-500' },
  'sent':        { label: 'נשלח',        bg: 'bg-blue-100',     text: 'text-blue-900',    dot: 'bg-blue-600' },
  'proposal':    { label: 'הצעה',        bg: 'bg-blue-100',     text: 'text-blue-900',    dot: 'bg-blue-600' },
  'negotiation': { label: 'משא ומתן',    bg: 'bg-amber-100',    text: 'text-amber-900',   dot: 'bg-amber-600' },
  'closed-won':  { label: 'ניצח',         bg: 'bg-emerald-100',  text: 'text-emerald-900', dot: 'bg-emerald-600' },
  'won':         { label: 'ניצח',         bg: 'bg-emerald-100',  text: 'text-emerald-900', dot: 'bg-emerald-600' },
  'closed-lost': { label: 'הפסד',         bg: 'bg-red-100',      text: 'text-red-900',     dot: 'bg-red-600' },
  'lost':        { label: 'הפסד',         bg: 'bg-red-100',      text: 'text-red-900',     dot: 'bg-red-600' },
  'signed':      { label: 'נחתם',         bg: 'bg-primary/10',   text: 'text-primary',     dot: 'bg-primary' },
  'pending':     { label: 'ממתין',        bg: 'bg-amber-100',    text: 'text-amber-900',   dot: 'bg-amber-600' },
  'expired':     { label: 'פג תוקף',      bg: 'bg-slate-100',    text: 'text-slate-700',   dot: 'bg-slate-500' },

  // Customer types
  'lead':        { label: 'ליד',          bg: 'bg-orange-100',   text: 'text-orange-900',  dot: 'bg-orange-600' },
  'customer':    { label: 'לקוח',         bg: 'bg-emerald-100',  text: 'text-emerald-900', dot: 'bg-emerald-600' },
  'active':      { label: 'פעיל',         bg: 'bg-emerald-100',  text: 'text-emerald-900', dot: 'bg-emerald-600' },
  'inactive':    { label: 'לא פעיל',      bg: 'bg-slate-100',    text: 'text-slate-700',   dot: 'bg-slate-400' },
  'closed':      { label: 'סגור',         bg: 'bg-slate-200',    text: 'text-slate-800',   dot: 'bg-slate-600' },

  // Payment
  'paid':        { label: 'שולם',         bg: 'bg-emerald-100',  text: 'text-emerald-900', dot: 'bg-emerald-600' },
  'unpaid':      { label: 'לא שולם',      bg: 'bg-amber-100',    text: 'text-amber-900',   dot: 'bg-amber-600' },
  'overdue':     { label: 'איחור',        bg: 'bg-red-100',      text: 'text-red-900',     dot: 'bg-red-600' },
};

export function getStatusStyle(status: string | null | undefined): StatusStyle {
  const key = (status || 'open').toLowerCase() as StatusKey;
  return STATUS_STYLES[key] || STATUS_STYLES['open'];
}

// Avatar color palette — deterministic assignment based on name
// Produces a stable color per person without needing to store it.
const AVATAR_PALETTE = [
  { bg: 'bg-primary/10',   text: 'text-primary' },
  { bg: 'bg-emerald-100',  text: 'text-emerald-700' },
  { bg: 'bg-amber-100',    text: 'text-amber-700' },
  { bg: 'bg-pink-100',     text: 'text-pink-700' },
  { bg: 'bg-purple-100',   text: 'text-purple-700' },
  { bg: 'bg-sky-100',      text: 'text-sky-700' },
  { bg: 'bg-orange-100',   text: 'text-orange-700' },
  { bg: 'bg-teal-100',     text: 'text-teal-700' },
] as const;

export function getAvatarColors(seed: string): { bg: string; text: string } {
  if (!seed) return AVATAR_PALETTE[0];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = ((hash << 5) - hash) + seed.charCodeAt(i);
    hash |= 0;
  }
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
}

// Initials from a person's name (handles Hebrew + English)
export function getInitials(name: string | null | undefined): string {
  if (!name) return '?';
  const trimmed = name.trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2);
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Format money (Israeli shekel)
export function formatMoney(value: number | null | undefined, options?: { compact?: boolean }): string {
  if (value === null || value === undefined) return '—';
  if (options?.compact && Math.abs(value) >= 1000) {
    return '₪' + (value / 1000).toFixed(1) + 'K';
  }
  return '₪' + Number(value).toLocaleString('he-IL');
}

// Format date (Hebrew locale)
export function formatDate(date: string | Date | null | undefined, options?: { relative?: boolean; withTime?: boolean }): string {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '—';

  if (options?.relative) {
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 1) return 'עכשיו';
    if (mins < 60) return `לפני ${mins} דקות`;
    if (hours < 24) return `לפני ${hours} שעות`;
    if (days < 7) return `לפני ${days} ימים`;
    return d.toLocaleDateString('he-IL');
  }

  if (options?.withTime) {
    return d.toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' });
  }
  return d.toLocaleDateString('he-IL');
}
